import { Separator } from '@components/ui/separator';
import { Heading } from '@components/ui/typography/heading';
import { Text } from '@components/ui/typography/text';
import { VStack, Flex } from '@components/ui/layout/flex';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@components/ui/table';

export default function CASupplement() {
  return (
    <div className="animate-in fade-in duration-300">
      <VStack className="min-h-screen flex-col md:flex-row">
        <Flex className="w-full" align="start" justify="start">
          <div>
            <div>
              <Heading level={2}>California Supplement</Heading>
            </div>
            <div className="pb-5">
              <Heading level={4} className="py-4 text-gray-800 font-medium">
                Supplemental Notice for California Residents
              </Heading>
              <Text className="text-gray-500 text-sm italic font-semibold">
                Effective Date: May 4, 2026
              </Text>
            </div>

            <Text className="text-lg pb-4">
              This Supplemental Notice supplements the information in our Privacy Policy, and
              except as provided herein, applies solely to California residents. It applies to
              personal information we collect on or through the Site or Service and through other
              means (such as information collected offline, in person, and over video calls). It
              does not apply to personal information we collect from our employees and job
              applicants in their capacity as employees and job applicants. It also does not apply
              to personal information we process as a service provider. Any capitalized terms used
              but not defined in this Supplemental Notice have the meanings set forth in our
              Privacy Policy.
            </Text>

            <Separator />
            <Text className="text-lg py-4">
              If you are a California resident, California law requires us to provide you with some
              additional information regarding how we collect, use, and disclose your
              &quot;personal information&quot; and &quot;sensitive personal information&quot; (as
              defined in the California Consumer Privacy Act (&quot;CCPA&quot;)).
            </Text>
            <Text className="text-lg py-4">
              Throughout our Privacy Policy, we describe the specific pieces of personal
              information and sensitive personal information we collect, the sources of that
              information, and how we disclose it.
            </Text>
            <Text className="text-gray-500 font-medium text-base pb-4">
              Under the CCPA, we are also required to provide you with the &quot;categories&quot;
              of personal information we collect and disclose. Those categories of personal
              information are as set forth in the table below. We collect and disclose those
              categories of personal information for the purposes described in our Privacy Policy,
              including for the business or commercial purposes (as those terms are defined in
              applicable law) set forth in the table below.
            </Text>

            <div className="overflow-x-auto p-4 border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Categories of Personal Information</TableHead>
                    <TableHead>Business or Commercial Purposes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Text className="font-bold">Identifiers</Text>
                      <Text>(e.g., name, email address, phone number)</Text>
                    </TableCell>
                    <TableCell rowSpan={2} className="align-top">
                      <ul className="list-disc pl-4 space-y-2">
                        <li>Operate and provide our Site or Service</li>
                        <li>Audit interactions on our Site or Service</li>
                        <li>Prevent fraud</li>
                        <li>Enhance security</li>
                        <li>Detect bugs and errors</li>
                        <li>Analyze use of, or improve, the Site or Service</li>
                        <li>Customize displayed content</li>
                        <li>Comply with laws; defend our legal rights</li>
                        <li>Other uses about which we notify you</li>
                      </ul>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Text className="font-bold">Inference data about you</Text>
                      <Text>(e.g., browser type, OS, language preference)</Text>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>

            <div>
              <Heading level={6} className="text-gray-500 font-extrabold pt-6">
                Data
              </Heading>
              <Text className="text-gray-500 text-base italic font-extrabold pt-5">
                Our sources of personal information.
              </Text>
              <Text>
                We collect the categories of personal information identified above from the
                following sources: (1) directly from you; (2) through your use of the Site or
                Service; and (3) providers.
              </Text>
              <Text className="text-gray-500 text-base italic font-extrabold pt-5">
                We do not practice medicine
              </Text>
              <Text>
                We do not control or take any responsibility for the practice of medicine by any
                Provider, each of whom is solely responsible for the medical care and treatment it
                provides to you. We make no representations or warranties about the suitability,
                reliability, timeliness or accuracy of the medical care and treatment provided by
                any Provider. You acknowledge and agree that we are not a healthcare provider and
                you are not entering into a doctor-patient or other health care provider-patient
                relationship with us.
              </Text>
              <Text>
                Note: When we process health information as a HIPAA Business Associate, that
                information may be exempt from certain CCPA requirements and is governed by our
                Privacy Policy&apos;s HIPAA provisions.
              </Text>
              <Text className="text-gray-500 text-base italic font-extrabold pt-5">
                Aggregation and de-identification
              </Text>
              <Text className="font-bold">
                We may combine the information we collect (&quot;aggregate&quot;) or remove pieces
                of information (&quot;de-identify&quot;) to limit or prevent identification of any
                particular user or device.
              </Text>

              <Text className="text-gray-500 text-base italic font-extrabold pt-5">
                Our information disclosure practices.
              </Text>
              <Text>
                We describe our information disclosure practices in our Privacy Policy. We may
                disclose certain categories of personal information with third parties (as defined
                by the CCPA) for the business purposes described above.
              </Text>

              <Text className="text-gray-500 text-base italic font-extrabold pt-5">
                Do not sell or share
              </Text>
              <Text>
                We do not sell or share (as such terms are defined in the CCPA) your personal
                information to third parties.
              </Text>
            </div>

            <div>
              <Heading level={6} className="text-gray-500 font-extrabold pt-6">
                Your Rights
              </Heading>

              <Text className="text-base font-extrabold pt-5">
                If you are a California resident, you may have certain rights. California law may
                permit you to request that we:
              </Text>
              <ul className="list-disc pl-6 space-y-3 mb-4">
                <li>
                  <Text className="pt-3">
                    Provide you the categories of personal information we have collected or
                    disclosed about you; the categories of sources of such information; the
                    business or commercial purpose for collecting, &quot;selling,&quot; or
                    &quot;sharing&quot; your personal information; the categories of third parties
                    to whom we disclose or &quot;sell,&quot; or with whom we &quot;share,&quot;
                    personal information; and the categories of personal information we
                    &quot;sell.&quot;
                  </Text>
                </li>
                <li>
                  <Text className="pb-3">
                    Provide access to and/or a copy of certain information we hold about you.
                  </Text>
                </li>
                <li>
                  <Text className="pb-3">Delete certain information we have about you.</Text>
                </li>
                <li>
                  <Text className="pb-3">
                    Correct inaccurate personal information that we maintain about you.
                  </Text>
                </li>
              </ul>
              <Text>
                You also have the right to not be discriminated against (as provided for in
                applicable law) for exercising certain of your rights. Certain information may be
                exempt from such requests under applicable law. We need certain types of
                information so that we can provide the Site or Service to you. If you ask us to
                delete it, you may no longer be able to access or use the Site or Service.
              </Text>
            </div>

            <div>
              <Heading level={6} className="text-gray-500 font-extrabold pt-6">
                Information collected on behalf of our customers.
              </Heading>
              <Text className="py-5">
                In instances where we process personal information on behalf of our customer,
                rights requests should be directed to the relevant customer. Any request sent
                directly to us that pertains to information collected on behalf of a customer will
                be forwarded on to that customer.
              </Text>
            </div>

            <div>
              <Heading level={6} className="text-gray-500 font-extrabold pt-6">
                How to exercise your rights.
              </Heading>
              <Text className="pt-5">
                If you would like to exercise any of these rights, you can send an email with your
                request to privacy@abobotech.com. You will be required to verify your identity
                before we fulfill your request. To do so, you will need to provide information to
                match with our existing records to verify your identity depending on the nature of
                the request and the sensitivity of the information sought (e.g., provide your email
                address). You can also designate an authorized agent to make a request on your
                behalf. To do so, you must provide us with written authorization or a power of
                attorney, signed by you, for the agent to act on your behalf. You will still need
                to verify your identity directly with us. Any request sent directly to us that
                pertains to information collected on behalf of a customer will be forwarded on to
                that customer.
              </Text>
            </div>

            <div>
              <Heading level={6} className="text-gray-500 font-extrabold pt-6">
                Sensitive personal information
              </Heading>
              <Text className="text-gray-500 text-base italic font-extrabold pt-5">
                The CCPA also allows you to limit the use or disclosure of your &quot;sensitive
                personal information&quot; (as defined in the CCPA) if your sensitive personal
                information is used for certain purposes. Please note that we do not use or
                disclose sensitive personal information other than for purposes for which you
                cannot opt out under the CCPA.
              </Text>
            </div>
          </div>
        </Flex>
      </VStack>
    </div>
  );
}
